import React, { useEffect, useState } from 'react';
import { Header } from '@backstage/core-components';
import { Card, CardContent, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AccountsSummary from './AccountsSummary';
import CostOptimizationChart from './CostOptimizationChart';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';

const CostOptimization = () => {
  const [accountDetails, setAccountDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [org, setOrg] = useState('');
  const [role, setRole] = useState('')
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [isCardClicked, setIsCardClicked] = useState(false);
  const [filteredData, setFilteredData] = useState({});
  const [tenantList, setTenantList] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(''); 
  const identityApi = useApi(identityApiRef)

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenantsfromDb = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenantsfromDb);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let data = [];
        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/cost-optimization-summary`, { method: 'POST' });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          let response = await fetchresponse.json();
          let role = response.role
          setRole(role)
          data = response.data || []
          console.log('Fetched data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }

        const finalData = data.map(item => ({
          ...item,
          services: JSON.parse(item.services),
          servicePercentages: JSON.parse(item.servicePercentages),
          serviceSet: item.serviceSet.split(','),
        }));

        setAccountDetails(finalData);
        setIsLoading(false);

        // Extract tenants
        const tenants = Array.from(new Set(finalData.map(item => item.tenantName)));
        setTenantList(tenants);
        setSelectedTenant(''); // default: all tenants
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
    const orgName = localStorage.getItem('orgName');
    if (orgName) setOrg(orgName);
  }, []);

  const handleTenantChange = (event) => {
    setSelectedTenant(event.target.value);
  };

  const filteredAccountDetails = selectedTenant
    ? accountDetails.filter(account => account.tenantName === selectedTenant)
    : accountDetails;

  const handleCardClick = (accountId) => {
    setExpandedAccountId(expandedAccountId === accountId ? null : accountId);
    setIsCardClicked(true);
    const filteredData = accountDetails.find(account => account.accountId === accountId) || {};
    setFilteredData(filteredData);
  };

  const handleBackButtonClick = () => {
    setExpandedAccountId(null);
    setIsCardClicked(false);
    setFilteredData({});
  };


  return (
    <div style={{ marginBottom: '0px' }}>
      <Header title="Cost Optimization Summary" subtitle={org} />

      {/* Tenant dropdown right-aligned (same as KMS dropdown) */}
      {role === "SuperAdmin" && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0' }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel></InputLabel>
          <Select
            value={selectedTenant}
            onChange={handleTenantChange}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  // force vertical stacking
                  '& .MuiList-root': {
                    display: 'flex !important',
                    flexDirection: 'column !important',
                  },
                  // force left-aligned text inside items
                  '& .MuiMenuItem-root': {
                    justifyContent: 'flex-start',
                    pl: 2,
                  },
                },
              },
            }}
          >
            <MenuItem value="">All Tenants</MenuItem>
            {tenantsFromDb.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.name}>
                {tenant.name}
              </MenuItem>
              ))}
          </Select>
        </FormControl>
        </div>
      )}

      {!isCardClicked && (
        <div>
          {filteredAccountDetails && filteredAccountDetails.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card style={{ cursor: 'pointer' }}>
                  <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <AccountsSummary data={filteredAccountDetails} />
                  </CardContent>
                </Card>
              </Grid>

              {filteredAccountDetails.map((account) => (
                <Grid key={`${account.tenantName}-${account.accountId}`} item xs={12} md={6}>
                  <Card style={{ cursor: 'pointer' }} onClick={() => handleCardClick(account.accountId)}>
                    <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CostOptimizationChart
                        accountDetails={account}
                        smallVersion={true}
                        onBackClick={handleBackButtonClick}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Loader />
          )}
        </div>
      )}

      {isCardClicked && filteredData && Object.keys(filteredData).length > 0 && (
        <Grid>
          <Card>
            <CardContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CostOptimizationChart
                accountDetails={filteredData}
                smallVersion={false}
                onBackClick={handleBackButtonClick}
              />
            </CardContent>
          </Card>
        </Grid>
      )}
    </div>
  );
};

export default CostOptimization;